import React, { useState, useEffect, useMemo } from 'react';
import { bannersAPI, propertyAPI } from '../../lib/api';
import { GlassCard } from '../../components/common/GlassCard';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus,
  Image as ImageIcon,
  Edit2,
  Trash2,
  Link as LinkIcon,
  Video,
  ArrowRight,
  Calendar,
  Power,
  PowerOff,
  ArrowUp,
  ArrowDown,
  Upload,
} from 'lucide-react';

const BANNER_TYPES = [
  { id: 'link', label: 'Link (open external URL)', icon: LinkIcon },
  { id: 'embed_video', label: 'Link Video', icon: Video },
  { id: 'redirect_detail', label: 'Redirect to Detail (in-app)', icon: ArrowRight },
];

const EMPTY_FORM = {
  title: '',
  description: '',
  banner_type: 'redirect_detail',
  external_url: '',
  video_url: '',
  property_id: '',
  sort_order: 0,
  is_active: true,
  valid_from: '',
  valid_until: '',
  eligible_roles: [],
};

const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

const typeLabel = (type) => BANNER_TYPES.find((t) => t.id === type)?.label || type;

export default function AdminBanners() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [filterType, setFilterType] = useState('');
  const [properties, setProperties] = useState([]);

  const loadBanners = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType) params.banner_type = filterType;
      const res = await bannersAPI.adminList(params);
      setBanners(res.data.banners || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load banners');
    } finally {
      setLoading(false);
    }
  };

  const loadProperties = async () => {
    try {
      const res = await propertyAPI.getProperties({ limit: 500 });
      const list = res.data?.properties || res.data || [];
      setProperties(Array.isArray(list) ? list : []);
    } catch {
      /* properties dropdown is optional; ignore failure */
    }
  };

  useEffect(() => {
    loadBanners();
  }, [filterType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadProperties();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setImageFile(null);
    setImagePreview(null);
    setShowForm(true);
  };

  const openEdit = (banner) => {
    setEditing(banner);
    setForm({
      title: banner.title || '',
      description: banner.description || '',
      banner_type: banner.banner_type || 'redirect_detail',
      external_url: banner.external_url || '',
      video_url: banner.video_url || '',
      property_id: banner.property_id || '',
      sort_order: banner.sort_order ?? 0,
      is_active: banner.is_active !== false,
      valid_from: banner.valid_from ? banner.valid_from.split('T')[0] : '',
      valid_until: banner.valid_until ? banner.valid_until.split('T')[0] : '',
      eligible_roles: banner.eligible_roles || [],
    });
    setImageFile(null);
    setImagePreview(banner.image_url || null);
    setShowForm(true);
  };

  const onPickImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please pick an image file');
      return;
    }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target?.result);
    reader.readAsDataURL(file);
  };

  const validateBeforeSave = () => {
    if (!form.title.trim()) return 'Title is required';
    if (!form.banner_type) return 'Banner type is required';
    if (form.banner_type === 'link' && !form.external_url.trim()) return 'Redirect URL is required for Link type';
    if (form.banner_type === 'embed_video' && !form.video_url.trim()) return 'Video URL is required for Link Video type';
    if (form.valid_from && form.valid_until && form.valid_from > form.valid_until) {
      return 'Start date must be before end date';
    }
    return null;
  };

  const handleSave = async () => {
    const errMsg = validateBeforeSave();
    if (errMsg) {
      toast.error(errMsg);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        sort_order: Number(form.sort_order) || 0,
        valid_from: form.valid_from ? new Date(form.valid_from).toISOString() : null,
        valid_until: form.valid_until ? new Date(form.valid_until).toISOString() : null,
        external_url: form.banner_type === 'link' ? form.external_url : null,
        video_url: form.banner_type === 'embed_video' ? form.video_url : null,
        property_id: form.banner_type === 'redirect_detail' ? form.property_id || null : null,
      };

      let bannerId;
      if (editing) {
        const res = await bannersAPI.adminUpdate(editing.id, payload);
        bannerId = res.data.id || editing.id;
        toast.success('Banner updated');
      } else {
        const res = await bannersAPI.adminCreate(payload);
        bannerId = res.data.id;
        toast.success('Banner created');
      }

      // Upload image if a new file was picked
      if (imageFile && bannerId) {
        await bannersAPI.adminUploadImage(bannerId, imageFile);
      }

      setShowForm(false);
      loadBanners();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : detail?.message || 'Failed to save banner';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (banner) => {
    if (!window.confirm(`Delete banner "${banner.title}"? This cannot be undone.`)) return;
    try {
      await bannersAPI.adminDelete(banner.id);
      toast.success('Banner deleted');
      loadBanners();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const toggleActive = async (banner) => {
    try {
      await bannersAPI.adminUpdate(banner.id, { is_active: !banner.is_active });
      toast.success(banner.is_active ? 'Banner deactivated' : 'Banner activated');
      loadBanners();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const moveSort = async (banner, delta) => {
    try {
      const newOrder = Math.max(0, (banner.sort_order ?? 0) + delta);
      await bannersAPI.adminUpdate(banner.id, { sort_order: newOrder });
      loadBanners();
    } catch {
      toast.error('Failed to update sort order');
    }
  };

  const sortedBanners = useMemo(
    () => [...banners].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    [banners]
  );

  return (
    <div className="space-y-6" data-testid="admin-banners-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Banners</h1>
          <p className="text-gray-500 mt-1">
            CMS-managed promotional banners shown on the mobile app homepage carousel.
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-[#4E50FF] hover:bg-[#3E40EE]"
          data-testid="create-banner-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Banner
        </Button>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="banner-type-filter">
        <button
          onClick={() => setFilterType('')}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
            !filterType ? 'bg-[#4E50FF] text-white border-[#4E50FF]' : 'bg-white text-gray-600 border-gray-200'
          }`}
        >
          All Types
        </button>
        {BANNER_TYPES.map((t) => (
          <button
            key={t.id}
            onClick={() => setFilterType(t.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
              filterType === t.id
                ? 'bg-[#4E50FF] text-white border-[#4E50FF]'
                : 'bg-white text-gray-600 border-gray-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#4E50FF]" />
        </div>
      ) : sortedBanners.length === 0 ? (
        <GlassCard className="text-center py-12" data-testid="banner-empty-state">
          <ImageIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No banners yet</p>
          <Button onClick={openCreate} className="mt-4 bg-[#4E50FF] hover:bg-[#3E40EE]">
            Create First Banner
          </Button>
        </GlassCard>
      ) : (
        <div className="space-y-3" data-testid="admin-banners-list">
          {sortedBanners.map((banner) => (
            <GlassCard
              key={banner.id}
              className="flex items-center gap-4 p-4"
              data-testid={`admin-banner-${banner.id}`}
            >
              <div className="w-20 h-14 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                {banner.image_url ? (
                  <img
                    src={banner.image_url.startsWith('/') ? `/api${banner.image_url}` : banner.image_url}
                    alt={banner.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <ImageIcon className="w-6 h-6 text-gray-400" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-gray-900 truncate">{banner.title}</h3>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      banner.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'
                    }`}
                  >
                    {banner.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <span className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full">
                    {typeLabel(banner.banner_type)}
                  </span>
                  <span className="text-xs text-gray-500">Sort: {banner.sort_order ?? 0}</span>
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 flex-wrap">
                  {banner.property_name && (
                    <span className="flex items-center gap-1">→ {banner.property_name}</span>
                  )}
                  {banner.external_url && (
                    <span className="truncate max-w-xs">URL: {banner.external_url}</span>
                  )}
                  {banner.video_url && <span className="truncate max-w-xs">Video: {banner.video_url}</span>}
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(banner.valid_from)} → {formatDate(banner.valid_until)}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => moveSort(banner, -1)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                  title="Move up"
                  data-testid={`banner-up-${banner.id}`}
                >
                  <ArrowUp className="w-4 h-4 text-gray-500" />
                </button>
                <button
                  onClick={() => moveSort(banner, 1)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                  title="Move down"
                  data-testid={`banner-down-${banner.id}`}
                >
                  <ArrowDown className="w-4 h-4 text-gray-500" />
                </button>
                <button
                  onClick={() => toggleActive(banner)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                  title={banner.is_active ? 'Deactivate' : 'Activate'}
                  data-testid={`banner-toggle-${banner.id}`}
                >
                  {banner.is_active ? (
                    <Power className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <PowerOff className="w-4 h-4 text-gray-400" />
                  )}
                </button>
                <button
                  onClick={() => openEdit(banner)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                  title="Edit"
                  data-testid={`banner-edit-${banner.id}`}
                >
                  <Edit2 className="w-4 h-4 text-blue-600" />
                </button>
                <button
                  onClick={() => handleDelete(banner)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                  title="Delete"
                  data-testid={`banner-delete-${banner.id}`}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </button>
              </div>
            </GlassCard>
          ))}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent
          className="max-w-2xl max-h-[90vh] overflow-y-auto"
          data-testid="banner-form-dialog"
        >
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Banner' : 'New Banner'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="banner-title">Title *</Label>
              <Input
                id="banner-title"
                data-testid="banner-title-input"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Headline shown above the banner image"
              />
            </div>

            <div>
              <Label htmlFor="banner-desc">Description</Label>
              <textarea
                id="banner-desc"
                data-testid="banner-description-input"
                rows={3}
                className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4E50FF]"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Optional caption (shown on banner detail page)"
              />
            </div>

            <div>
              <Label>Banner Type *</Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-1">
                {BANNER_TYPES.map((t) => {
                  const Icon = t.icon;
                  const active = form.banner_type === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setForm({ ...form, banner_type: t.id })}
                      className={`p-3 rounded-lg border text-left text-sm transition ${
                        active
                          ? 'bg-[#4E50FF]/5 border-[#4E50FF] text-[#4E50FF]'
                          : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                      }`}
                      data-testid={`banner-type-${t.id}`}
                    >
                      <Icon className="w-4 h-4 mb-1" />
                      <div className="font-medium">{t.label}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {form.banner_type === 'link' && (
              <div>
                <Label htmlFor="banner-url">Redirect URL *</Label>
                <Input
                  id="banner-url"
                  data-testid="banner-external-url-input"
                  value={form.external_url}
                  onChange={(e) => setForm({ ...form, external_url: e.target.value })}
                  placeholder="https://…"
                />
              </div>
            )}

            {form.banner_type === 'embed_video' && (
              <div>
                <Label htmlFor="banner-video">Video URL *</Label>
                <Input
                  id="banner-video"
                  data-testid="banner-video-url-input"
                  value={form.video_url}
                  onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                  placeholder="YouTube/Vimeo URL"
                />
              </div>
            )}

            {form.banner_type === 'redirect_detail' && (
              <div>
                <Label htmlFor="banner-prop">Property Reference (optional)</Label>
                <select
                  id="banner-prop"
                  data-testid="banner-property-select"
                  className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4E50FF]"
                  value={form.property_id}
                  onChange={(e) => setForm({ ...form, property_id: e.target.value })}
                >
                  <option value="">— None (generic banner) —</option>
                  {properties.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Linking a property enriches the mobile detail page with the property card.
                </p>
              </div>
            )}

            <div>
              <Label>Banner Image</Label>
              <div className="mt-1 flex items-center gap-3">
                <label
                  htmlFor="banner-image-file"
                  className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
                >
                  <Upload className="w-4 h-4" />
                  {imageFile ? 'Replace image' : 'Upload image'}
                </label>
                <input
                  id="banner-image-file"
                  data-testid="banner-image-input"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={onPickImage}
                />
                {imageFile && <span className="text-xs text-gray-500">{imageFile.name}</span>}
              </div>
              {imagePreview && (
                <div className="mt-2 w-full max-w-md aspect-[16/9] bg-gray-100 rounded-lg overflow-hidden">
                  <img
                    src={
                      imagePreview.startsWith('data:') || imagePreview.startsWith('http')
                        ? imagePreview
                        : `/api${imagePreview}`
                    }
                    alt="preview"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="banner-from">Start Date</Label>
                <Input
                  id="banner-from"
                  data-testid="banner-valid-from-input"
                  type="date"
                  value={form.valid_from}
                  onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="banner-until">End Date</Label>
                <Input
                  id="banner-until"
                  data-testid="banner-valid-until-input"
                  type="date"
                  value={form.valid_until}
                  onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="banner-sort">Sort Order</Label>
                <Input
                  id="banner-sort"
                  data-testid="banner-sort-order-input"
                  type="number"
                  min={0}
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                />
              </div>
              <div className="flex items-end">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="banner-active-checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    className="w-4 h-4 accent-[#4E50FF]"
                  />
                  <span className="text-sm text-gray-700">Active (visible to mobile users)</span>
                </label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="bg-[#4E50FF] hover:bg-[#3E40EE]"
              disabled={saving}
              data-testid="banner-save-btn"
            >
              {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Banner'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
